import { type ReactNode, useEffect, useState } from 'react';
import { Inbox } from 'lucide-react';

// The Ops kit: the same doctrine as apps/hq's kit — a small set of composed
// pieces, every row opens something, every empty state is designed — wearing
// the night-control-room language from styles/ops.css.

export function Screen(props: {
  eyebrow?: string;
  title: string;
  sub?: ReactNode;
  actions?: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={`ops-screen${props.className ? ` ${props.className}` : ''}`}>
      <header className="ops-screen__head">
        <div className="ops-screen__titles">
          {props.eyebrow ? <p className="ops-screen__eyebrow">{props.eyebrow}</p> : null}
          <h1 className="ops-screen__title">{props.title}</h1>
          {props.sub ? <p className="ops-screen__sub">{props.sub}</p> : null}
        </div>
        {props.actions ? <div className="ops-screen__actions">{props.actions}</div> : null}
      </header>
      {props.children}
    </div>
  );
}

export function Section(props: { title: string; aside?: ReactNode; children: ReactNode }) {
  return (
    <section className="ops-section">
      <h2 className="ops-section__title">
        <span>{props.title}</span>
        {props.aside ? <span className="ops-section__aside">{props.aside}</span> : null}
      </h2>
      {props.children}
    </section>
  );
}

export function Card(props: { onTap?: () => void; className?: string; children: ReactNode }) {
  const cls = `ops-card${props.onTap ? ' ops-card--tap' : ''}${props.className ? ` ${props.className}` : ''}`;
  if (props.onTap) {
    return (
      <button type="button" className={cls} onClick={props.onTap}>
        {props.children}
      </button>
    );
  }
  return <div className={cls}>{props.children}</div>;
}

export function Rows(props: { children: ReactNode }) {
  return <div className="ops-rows">{props.children}</div>;
}

export function Row(props: {
  title: ReactNode;
  sub?: ReactNode;
  end?: ReactNode;
  onTap?: () => void;
}) {
  const inner = (
    <>
      <div className="ops-row__main">
        <div className="ops-row__title">{props.title}</div>
        {props.sub ? <div className="ops-row__sub">{props.sub}</div> : null}
      </div>
      {props.end ? <div className="ops-row__end">{props.end}</div> : null}
    </>
  );
  if (props.onTap) {
    return (
      <button type="button" className="ops-row" onClick={props.onTap}>
        {inner}
      </button>
    );
  }
  return <div className="ops-row">{inner}</div>;
}

export function Btn(props: {
  kind?: 'primary' | 'quiet' | 'danger' | 'ghost';
  big?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  children: ReactNode;
  'data-testid'?: string;
}) {
  return (
    <button
      type="button"
      className={`ops-btn ops-btn--${props.kind ?? 'quiet'}${props.big ? ' ops-btn--big' : ''}`}
      disabled={props.disabled}
      onClick={props.onClick}
      data-testid={props['data-testid']}
    >
      {props.children}
    </button>
  );
}

export type Tone = 'wait' | 'run' | 'ok' | 'bad' | 'hold' | 'accent';

export function Badge(props: { tone: Tone; pulse?: boolean; children: ReactNode; testId?: string }) {
  return (
    <span className={`ops-badge ops-badge--${props.tone}`} data-testid={props.testId}>
      {props.pulse ? <span className="ops-badge__pulse" /> : null}
      {props.children}
    </span>
  );
}

export function Seg<T extends string>(props: {
  value: T;
  options: { value: T; label: ReactNode; ship?: boolean }[];
  onChange: (v: T) => void;
  big?: boolean;
}) {
  return (
    <div className={`ops-seg${props.big ? ' ops-compose__modebig' : ''}`}>
      {props.options.map((o) => (
        <button
          key={o.value}
          type="button"
          className={`ops-seg__opt${o.ship ? ' ops-seg__opt--ship' : ''}`}
          aria-pressed={props.value === o.value}
          onClick={() => props.onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Field(props: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className="ops-field">
      <span className="ops-field__label">{props.label}</span>
      {props.children}
      {props.hint ? <p className="ops-hint">{props.hint}</p> : null}
    </label>
  );
}

export function Toggle(props: { checked: boolean; onChange: (v: boolean) => void; label?: string; disabled?: boolean; testId?: string }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={props.checked}
      aria-label={props.label}
      className="ops-toggle"
      disabled={props.disabled}
      data-testid={props.testId}
      onClick={() => props.onChange(!props.checked)}
    />
  );
}

/* A model id as a person reads it: `claude-opus-5-5` is "Claude Opus 5.5",
   `claude-haiku-4-5-20251001` is "Claude Haiku 4.5". The id itself is
   always shown beside it where it matters; this is only the name. */
export function modelName(id: string | null | undefined): string {
  if (!id) return '';
  const m = id.replace(/\[[^\]]*\]$/, '').match(/^claude-([a-z]+)-(\d+)(?:-(\d{1,2}))?(?:-\d{8})?$/i);
  if (!m) return id;
  const family = m[1].charAt(0).toUpperCase() + m[1].slice(1);
  return `Claude ${family} ${m[2]}${m[3] ? `.${m[3]}` : ''}`;
}

export function Sheet(props: { title: string; onClose: () => void; children: ReactNode }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') props.onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [props]);
  return (
    <>
      <div className="ops-sheet-backdrop" onClick={props.onClose} />
      <div className="ops-sheet" role="dialog" aria-modal="true" aria-label={props.title}>
        <div className="ops-sheet__grab" />
        <div className="ops-sheet__head">
          <h2 className="ops-sheet__title">{props.title}</h2>
          <Btn kind="ghost" onClick={props.onClose}>
            Close
          </Btn>
        </div>
        {props.children}
      </div>
    </>
  );
}

export function EmptyState(props: { icon?: ReactNode; title: string; children?: ReactNode }) {
  return (
    <div className="ops-empty">
      {props.icon ?? <Inbox />}
      <p className="ops-empty__title">{props.title}</p>
      {props.children ? <p>{props.children}</p> : null}
    </div>
  );
}

export function Facts(props: { items: { label: string; value: ReactNode; mono?: boolean }[] }) {
  return (
    <div className="ops-facts">
      {props.items.map((f, i) => (
        <div key={i} className="ops-fact">
          <div className="ops-fact__label">{f.label}</div>
          <div className={`ops-fact__value${f.mono ? ' ops-fact__value--mono' : ''}`}>{f.value}</div>
        </div>
      ))}
    </div>
  );
}

export function Sha(props: { value: string | null | undefined }) {
  if (!props.value) return null;
  return <span className="ops-sha">{props.value.slice(0, 8)}</span>;
}

export function Spinner() {
  return <span className="ops-spin" aria-label="Working" />;
}

export function ago(iso: string | null | undefined): string {
  if (!iso) return '';
  const s = Math.max(0, (Date.now() - Date.parse(iso)) / 1000);
  if (s < 60) return 'now';
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

export function minutes(from: string | null | undefined, to?: string | null): string {
  if (!from) return '';
  const end = to ? Date.parse(to) : Date.now();
  const m = Math.max(0, Math.round((end - Date.parse(from)) / 60000));
  if (m < 60) return `${m} min`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

/* Wall-clock HH:MM in the reader's own timezone. The one formatter for
   "when did this happen", shared by the session log's entries and the
   offline banner's "as of", so the two can never disagree about what a
   recorded time looks like. */
export function clock(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/* V8 §11. A duration computed once per poll looks frozen, and a duration
 * frozen at "15h 35m" through an hour of real work is exactly the complaint
 * this round exists to answer. This ticks every second while the thing is
 * still running, and stops the moment it is not. */
export function useNow(live: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!live) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [live]);
  return now;
}

/** "$12.34 · measured 14:32" — or, when that is old, why it has not moved. */
export function CostReading(props: { usd: number | null; asOf: string | null; live: boolean }) {
  const now = useNow(props.live);
  if (props.usd == null) return <span className="ops-cost ops-cost--none">cost not measured yet</span>;
  const age = props.asOf ? now - Date.parse(props.asOf) : 0;
  const stale = props.live && props.asOf && age > 5 * 60_000;
  return (
    <span className={`ops-cost${stale ? ' ops-cost--stale' : ''}`} title={props.asOf ? `measured at ${clock(props.asOf)}` : undefined}>
      ${props.usd.toFixed(2)}
      {props.asOf ? (
        <span className="ops-cost__as">
          {stale
            ? ` as of ${clock(props.asOf)} — this turn has not reported since`
            : ` as of ${clock(props.asOf)}`}
        </span>
      ) : null}
    </span>
  );
}
